/**
 * V2AdminHelpPage.jsx — "How TravelTrucks OS works"
 * ─────────────────────────────────────────────────────────────────────────
 * One staff-readable screen mapping the whole flow, from a new enquiry to a
 * finished rig. Linked from the OS nav as ❔ How this works.
 * Uses the existing V2OSChrome + palette so it feels like the rest of the OS.
 * Nothing on this page mutates state — it's pure explainer.
 */
import React from 'react';
import { V2OSChrome, V2OSC as C } from './V2AdminOSPage';

const ACCENT = 'var(--tt-orange)';

const StepCard = ({ n, title, body, testid }) => (
  <div data-testid={testid} style={{
    padding: '18px 20px', background: C.surface, border: `1px solid ${C.hair}`,
    borderRadius: 12, marginBottom: 12, wordBreak: 'break-word', overflowWrap: 'anywhere',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
      <div style={{ width: 30, height: 30, borderRadius: 999, display: 'flex',
                     alignItems: 'center', justifyContent: 'center',
                     background: 'rgba(236,124,36,0.14)', color: ACCENT,
                     fontFamily: 'Fredoka, sans-serif', fontWeight: 700, fontSize: 14 }}>
        {n}
      </div>
      <h3 style={{ fontFamily: 'Fredoka, sans-serif', fontSize: 17, color: C.icy, margin: 0,
                    fontWeight: 600, letterSpacing: -0.2 }}>{title}</h3>
    </div>
    <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.55, margin: '4px 0 0 42px' }}>{body}</p>
  </div>
);

const SectionCard = ({ title, purpose, testid }) => (
  <div data-testid={testid} style={{
    padding: '16px 18px', background: C.surface, border: `1px solid ${C.hair}`,
    borderRadius: 10, marginBottom: 10,
  }}>
    <div style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy, fontSize: 15, marginBottom: 4 }}>
      {title}
    </div>
    <div style={{ color: C.muted, fontSize: 13, lineHeight: 1.55 }}>{purpose}</div>
  </div>
);

export default function V2AdminHelpPage() {
  return (
    <V2OSChrome activeTab="help">
      <div data-testid="os-help-page" style={{ maxWidth: 820, margin: '0 auto', width: '100%',
                                                 wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
        <p style={{ fontFamily: 'var(--ff-caps)', color: C.faint, fontSize: 12,
                     letterSpacing: '0.18em', textTransform: 'uppercase' }}>
          TravelTrucks OS · How this works
        </p>
        <h1 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 600, color: C.icy,
                      fontSize: 'clamp(28px,4vw,38px)', lineHeight: 1.1, marginTop: 6,
                      marginBottom: 12 }}>
          How TravelTrucks OS <span style={{ color: ACCENT, fontStyle: 'italic', fontWeight: 400 }}>works</span>
        </h1>
        <p style={{ color: C.muted, fontSize: 15, marginBottom: 30, maxWidth: 640 }}>
          One page for whoever's running the board — from a new enquiry to a finished rig.
          Nothing sends without you tapping a confirmation button.
        </p>

        <h2 style={{ fontFamily: 'Fredoka, sans-serif', color: C.icy, fontSize: 22, marginBottom: 14,
                      letterSpacing: -0.2 }}>
          The flow — enquiry to finished rig
        </h2>

        <StepCard n="1" testid="help-step-1" title="An enquiry comes in"
          body="Someone wants a quote — a website 'get a quote' form, a phone call, or a walk-in. It lands as a new enquiry (via GoHighLevel or the website) and shows up in Today's pulse and the Customers list." />
        <StepCard n="2" testid="help-step-2" title="You build a quote"
          body="Open Quotes and put together a build quote — bullbar, suspension, snorkel, canopy, dual battery, whatever the rig needs. Line items, GST, a total. Send it from the OS and the customer gets a branded quote link." />
        <StepCard n="3" testid="help-step-3" title="They accept and pay a deposit"
          body="When the customer accepts and the deposit's in (Stripe / Xero), the quote becomes a job and moves onto the fitting-bay board. Honest money only — the tiles read live from Xero." />
        <StepCard n="4" testid="help-step-4" title="The build gets booked in"
          body="Book the rig into a bay with a date, and order the parts it needs. The job shows what's outstanding so nothing holds up the fit." />
        <StepCard n="5" testid="help-step-5" title="It moves through the fitting bay"
          body="The build works through the stages — Parts ordered → In bay → Fitting → QA → Ready for pickup. The Builds board shows every rig and exactly where it's at." />
        <StepCard n="6" testid="help-step-6" title="Delivered and invoiced"
          body="Rig's finished, the customer picks it up, and the final invoice goes out through Xero. The win lands on the Win Wall." />
        <StepCard n="7" testid="help-step-7" title="The 5pm debrief"
          body="At 5pm, Fieldy rolls up the day's real-world conversations — calls, walk-ins, workshop chatter — and suggests tasks and promises worth capturing. You tap to turn a suggestion into a real task; nothing is added automatically." />

        <h2 style={{ fontFamily: 'Fredoka, sans-serif', color: C.icy, fontSize: 22, margin: '36px 0 14px',
                      letterSpacing: -0.2 }}>
          What each section is for
        </h2>

        <SectionCard testid="help-sec-desk" title="The Desk (Base Camp)"
          purpose="Home — what needs you right now. The NOW card, today's pulse, the money ticker, the Win Wall, and your own draggable board of tiles. Rearrange it however you like; the layout saves." />
        <SectionCard testid="help-sec-customers" title="Customers"
          purpose="Everyone you've quoted or built for — owner and vehicle. Heat-ranked so the warm ones surface. Open a customer to see their rig, quotes and history in one place." />
        <SectionCard testid="help-sec-quotes" title="Quotes"
          purpose="Build quotes — compose, send, track and win. The Quote Pipeline moves each one through Proposal done → Col reviewed → Quote sent → Revision required → Quote accepted." />
        <SectionCard testid="help-sec-builds" title="Builds"
          purpose="Every rig in progress, by fitting-bay stage. See which bay, which stage, and what parts are still outstanding for each build." />
        <SectionCard testid="help-sec-connectors" title="Connectors (Admin)"
          purpose="Where Xero, Fieldy, Microsoft 365, GoHighLevel, Workflow Max and Stripe plug in. Connect a service once and its data flows straight into the tiles — no config files, no .env." />

        <h2 style={{ fontFamily: 'Fredoka, sans-serif', color: C.icy, fontSize: 22, margin: '36px 0 14px',
                      letterSpacing: -0.2 }}>
          Rules of thumb
        </h2>

        <div style={{ padding: '18px 20px', background: 'rgba(84,209,151,0.06)',
                       borderLeft: `3px solid #54D197`, borderRadius: 8, marginBottom: 12,
                       color: C.icy, fontSize: 14, lineHeight: 1.6 }}>
          <b style={{ color: '#54D197' }}>Safe to click:</b> anything that opens a preview, reads
          data, or navigates around — filter chips, mark-done, open a quote, download a PDF.
          Nothing sends an email or moves money from these.
        </div>
        <div style={{ padding: '18px 20px', background: 'rgba(236,124,36,0.08)',
                       borderLeft: `3px solid ${ACCENT}`, borderRadius: 8, marginBottom: 12,
                       color: C.icy, fontSize: 14, lineHeight: 1.6 }}>
          <b style={{ color: ACCENT }}>Confirmation-first:</b> anything labelled &quot;Send…&quot;
          fires a real email or invoice to a real customer. You&apos;ll always see a modal or
          confirmation step first — nothing sends by accident. If in doubt, cancel.
        </div>
        <div style={{ padding: '18px 20px', background: 'rgba(123,176,236,0.08)',
                       borderLeft: `3px solid #7BB0EC`, borderRadius: 8, marginBottom: 12,
                       color: C.icy, fontSize: 14, lineHeight: 1.6 }}>
          <b style={{ color: '#7BB0EC' }}>Honest data:</b> a tile with no connection says
          &quot;Connect Xero to see this&quot; — it never shows a fake $0. If a number looks
          empty, check the connector in Admin → Connectors.
        </div>
      </div>
    </V2OSChrome>
  );
}
